// React
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

// Libraries
import { parseISO } from 'date-fns';

// Form seam (GRAIN-2551)
import { useStore, type AnyFieldMeta } from '@tanstack/react-form';
import { Form } from '@components/Form';
import { useFormContext } from '@components/Form/FormProvider';
import { shouldShowFieldError, useFieldErrorVisibility } from '@components/Form';

// UI primitives
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipTrigger,
  TooltipProvider,
  TooltipContent,
} from '@/components/ui/tooltip';
import { Field, FieldLabel, FieldTitle, FieldContent, FieldError } from '@/components/ui/field';
import { CalendarDatePicker } from '@/components/ui/calendar-date-picker';
import { NumberStepper } from '@/components/ui/number-stepper';
import { Select, SelectTrigger, SelectContent, SelectItem } from '@/components/ui/select';

// Icons
import { ChevronDownIcon } from '@/react/components/icons/ChevronDownIcon';
import { MoreVerticalIcon } from '@/react/components/icons/MoreVerticalIcon';
import { TrashIcon } from '@/react/components/icons/TrashIcon';
import { DuplicateIcon } from '@/react/components/icons/DuplicateIcon';
import { InfoIcon } from '@/react/components/icons/InfoIcon';
import { StepperPlusIcon } from '@/react/components/icons/StepperPlusIcon';

// Hooks and utilities
import { useTranslation } from '@hooks/useTranslation';
import { useClosedDates } from '@hooks/operatingHours';
import { cn } from '@/lib/utils';
import { formatShortDate } from '@/react/utils/formatters';
import { toLocalDateKey } from '@/react/utils/dateTime';

// Local + shared components
import { ServingTimeDropdown } from '../ServingTimeDropdown/ServingTimeDropdown';
import { ServingLayoutInput } from './ServingLayoutInput';
import { useCreateEventActions } from './createEventActions';
import { ProductLinePicker, PRODUCT_LINE_LABELS } from '@components/common/ProductLinePicker';
import { EventTagsField } from '@components/common/EventTagsField';
import { InlineNotice } from '@components/common/InlineNotice';
import { eventFormValues, DEFAULT_FORM_LABELS } from '@components/Product/types';
import { useFeatureFlag } from '@hooks/useFeatures';
import { productLinesForForm } from '@components/common/ProductLinePicker/productLines';

// Types
import type { CreateEventFormData, CreateEventFormEvent } from '@/react/schemas/createEvent.schema';

import { FIELD_SURFACE } from "@/components/ui/field-surface";
export type EventFormState = CreateEventFormEvent;
export { createEmptyEvent, CreateEventActionsProvider } from './createEventActions';

/* -------------------------------------------------------------------------- */
/*  Constants                                                                  */
/* -------------------------------------------------------------------------- */

const TOOLTIP_DELAY_MS = 200;
const TEXTAREA_HEIGHT = 'h-[100px]';
const DROPDOWN_MENU_WIDTH = 'w-[232px]';

const EVENT_DIVIDER_CLASS = 'h-px bg-grey-light';

const LABEL_CLASS = 'font-inter text-sm font-semibold text-grey';
const INVALID_TRIGGER_CLASS = 'border-chilli-red-light bg-chilli-blush';
const TEXTAREA_CLASS = cn(
  cn(FIELD_SURFACE, 'font-inter resize-none px-6 py-4 text-sm text-off-black'),
  'transition-shadow placeholder:text-grey-light focus-visible:shadow-sm focus-visible:ring-grey/30',
  TEXTAREA_HEIGHT,
);
const DROPDOWN_ICON_SIZE = 'size-3.5';

// Adds nothing to "Kitchen closed", so the note and tooltip drop it.
const GENERIC_CLOSURE_REASON = 'closed';

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

// The day that gets submitted is the picker's browser wall-clock day (see
// `combineDateAndTime`), so the summary has to echo that same local day. Anchor
// it to noon Singapore before handing it to the shared formatter — which renders
// in Asia/Singapore — so the day can't slip and the shape matches every other
// date in the app ("03 Aug 2026", no weekday).
function formatSummaryDate(date: Date): string {
  return formatShortDate(`${toLocalDateKey(date)}T12:00:00+08:00`);
}

function formatServingTime(value: string): string {
  const [hours, minutes] = value.split(':').map(Number);
  const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const period = hours < 12 ? 'AM' : 'PM';
  return `${displayHour}:${String(minutes).padStart(2, '0')}${period}`;
}

function buildEventSummary(
  event: EventFormState,
  t: (key: string) => string,
): { title: string; details: string } | null {
  if (!event.form) return null;

  const titleParts = [DEFAULT_FORM_LABELS[event.form as keyof typeof DEFAULT_FORM_LABELS] ?? event.form];
  if (event.productLine) titleParts.push(PRODUCT_LINE_LABELS[event.productLine] ?? event.productLine);

  const detailParts: string[] = [];
  if (event.scheduledAt) detailParts.push(formatSummaryDate(event.scheduledAt));
  if (event.servingTime) detailParts.push(formatServingTime(event.servingTime));
  if (event.paxCount > 0) detailParts.push(`${event.paxCount} ${t('create_event.guests')}`);

  return {
    title: titleParts.join(' · '),
    details: detailParts.join(' · '),
  };
}

function closureLabel(reasons: string[], t: (key: string, params?: Record<string, string>) => string): string {
  const specific = reasons.filter((reason) => reason.toLowerCase() !== GENERIC_CLOSURE_REASON);
  return specific.length > 0
    ? t('create_event.kitchen_closed_with_reason', { reason: specific.join(', ') })
    : t('create_event.kitchen_closed');
}

const infoIcon = <InfoIcon />;

function LabelWithTooltip({ label, tooltip }: { label: string; tooltip: string }) {
  return (
    <FieldTitle className={LABEL_CLASS}>
      {label}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" aria-label="More info" className="p-0 text-grey hover:bg-transparent">
            {infoIcon}
          </Button>
        </TooltipTrigger>
        <TooltipContent align="center">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </FieldTitle>
  );
}

/* -------------------------------------------------------------------------- */
/*  EventFieldRow                                                              */
/* -------------------------------------------------------------------------- */

interface EventFieldRowRender<K extends keyof EventFormState> {
  value: EventFormState[K];
  onChange: (value: EventFormState[K]) => void;
  invalid: boolean;
}

interface EventFieldRowProps<K extends keyof EventFormState> {
  index: number;
  name: K;
  label: string;
  labelClassName?: string;
  fieldClassName?: string;
  children: (render: EventFieldRowRender<K>) => ReactNode;
}

// Label + control + FieldError for one `events[index]` field, and the single
// place the dynamic-index `as never` casts live. Unlike the seam's FieldRow it
// shows the message on an empty required field too, which GRAIN-2413 needs.
function EventFieldRow<K extends keyof EventFormState>({
  index,
  name,
  label,
  labelClassName,
  fieldClassName,
  children,
}: EventFieldRowProps<K>) {
  const { form } = useFormContext();
  const submitted = useFieldErrorVisibility();

  return (
    <form.Field name={`events[${index}].${name}` as never}>
      {(field) => {
        const meta = field.state.meta as AnyFieldMeta;
        const message = shouldShowFieldError(meta, submitted)
          ? (meta.errors?.[0] as { message?: string } | undefined)?.message ?? ''
          : '';
        return (
          <Field className={fieldClassName}>
            <FieldLabel className={labelClassName}>{label}</FieldLabel>
            <FieldContent>
              {children({
                value: field.state.value as EventFormState[K],
                onChange: (value) => field.handleChange(value as never),
                invalid: Boolean(message),
              })}
            </FieldContent>
            {message && <FieldError>{message}</FieldError>}
          </Field>
        );
      }}
    </form.Field>
  );
}

/* -------------------------------------------------------------------------- */
/*  CreateEventForm — body                                                     */
/* -------------------------------------------------------------------------- */

export function CreateEventForm() {
  const { form } = useFormContext();
  const { toggleExpanded } = useCreateEventActions();
  const events = useStore(form.store, (s) => (s.values as CreateEventFormData).events);
  const attempts = useStore(form.store, (s) => s.submissionAttempts);
  const isSubmitting = useStore(form.store, (s) => s.isSubmitting);
  const fieldMeta = useStore(form.store, (s) => s.fieldMeta);
  const revealedAttempt = useRef(0);
  useEffect(() => {
    if (attempts === revealedAttempt.current || isSubmitting) return;
    revealedAttempt.current = attempts;
    const index = events.findIndex((_, i) => Object.entries(fieldMeta).some(
      ([key, meta]) => key.startsWith(`events[${i}].`) && meta?.errors.length,
    ));
    if (index >= 0 && !events[index].expanded) toggleExpanded(index);
  }, [attempts, isSubmitting, fieldMeta, events, toggleExpanded]);

  return (
    <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
      <div className="flex flex-col gap-8">
        {events.map((event, index) => (
          <Fragment key={event.id}>
            {index > 0 && <div className={EVENT_DIVIDER_CLASS} />}
            <EventSection event={event} index={index} total={events.length} />
          </Fragment>
        ))}
      </div>
    </TooltipProvider>
  );
}

/* -------------------------------------------------------------------------- */
/*  EventSection                                                               */
/* -------------------------------------------------------------------------- */

interface EventSectionProps {
  event: EventFormState;
  index: number;
  total: number;
}

function EventSection({ event, index, total }: EventSectionProps) {
  const { t } = useTranslation();
  const { highlightedEventId, duplicateEvent, deleteEvent, toggleExpanded } = useCreateEventActions();

  const isHighlighted = event.id === highlightedEventId;
  const isMultipleEvents = total > 1;
  const isCollapsed = isMultipleEvents && !event.expanded;
  const summary = isCollapsed ? buildEventSummary(event, t) : null;

  return (
    <div className={cn('flex flex-col', isCollapsed ? 'gap-3' : 'gap-8')} data-event-id={event.id}>
      {/* Event header */}
      <div
        className={cn(
          "relative isolate flex items-center justify-between py-3",
          "before:content-[''] before:absolute before:-inset-x-3 before:inset-y-0 before:z-[-1] before:rounded-xs before:bg-body before:transition-colors before:duration-700 before:motion-reduce:transition-none",
          isMultipleEvents && !isCollapsed && 'sticky -top-8 z-10',
          isHighlighted && 'before:bg-custard-yellow',
        )}
      >
        <div className="flex items-center gap-2">
          <span className={cn(
            "inline-flex items-center rounded-xs border border-off-black px-2 py-0.5 font-inter text-sm font-normal text-off-black",
            isHighlighted && "animate-badge-in",
          )}>
            {t('create_event.event_label')} {index + 1}
          </span>
          {isMultipleEvents && (
            <Button
              variant="ghost"
              onClick={() => toggleExpanded(index)}
              aria-label={event.expanded ? 'Collapse event' : 'Expand event'}
              className="p-0 hover:bg-transparent"
            >
              <ChevronDownIcon
                aria-hidden="true"
                className={cn(
                  'text-off-black chevron-motion',
                  event.expanded && 'rotate-180',
                )}
              />
            </Button>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost-menu"
              size="icon-sm"
              aria-label="Event actions"
            >
              <MoreVerticalIcon
                aria-hidden="true"
                className="h-4 w-1 text-grey group-hover:text-off-black group-data-[state=open]:text-off-black"
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            onCloseAutoFocus={(e) => e.preventDefault()}
            className={cn("rounded-lg border-dinnerplate-white bg-off-white p-4 shadow-soft", DROPDOWN_MENU_WIDTH)}
          >
            {total > 1 && (
              <DropdownMenuItem
                onClick={() => deleteEvent(index)}
                className="flex items-center gap-1.5 rounded-xs p-1.5 font-inter text-sm font-semibold text-off-black cursor-pointer"
              >
                <TrashIcon aria-hidden="true" className={cn('shrink-0', DROPDOWN_ICON_SIZE)} />
                {t('create_event.delete')}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => duplicateEvent(index)}
              className="flex items-center gap-1.5 rounded-xs p-1.5 font-inter text-sm font-semibold text-off-black cursor-pointer"
            >
              <DuplicateIcon aria-hidden="true" className={cn('shrink-0', DROPDOWN_ICON_SIZE)} />
              {t('create_event.duplicate')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {summary && (
        <div className="flex flex-col gap-1">
          <p className="truncate font-inter text-sm font-semibold leading-normal text-off-black">
            {summary.title}
          </p>
          {summary.details && (
            <p className="font-inter text-sm leading-normal text-grey">
              {summary.details}
            </p>
          )}
        </div>
      )}

      {!isCollapsed && <EventFormFields event={event} index={index} />}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  EventFormFields                                                            */
/* -------------------------------------------------------------------------- */

interface EventFormFieldsProps {
  event: EventFormState;
  index: number;
}

function EventFormFields({ event, index }: EventFormFieldsProps) {
  const { t } = useTranslation();
  const { form } = useFormContext();
  const formValues = eventFormValues(useFeatureFlag('gathering_box'));

  const disabledFields = event.disabledFields ?? [];
  const anyFieldDisabled = (...fields: string[]) => fields.some((f) => disabledFields.includes(f));
  const fieldClass = (...fields: string[]) => cn(anyFieldDisabled(...fields) && 'pointer-events-none [&_[data-slot=field-content]>*]:!bg-dinnerplate-white-light');
  const labelClass = (...fields: string[]) => cn(LABEL_CLASS, anyFieldDisabled(...fields) && 'text-grey');

  // Writes for fields that carry no inline message (lines, tags, textareas) and
  // the format→product-line reset; validated fields write via EventFieldRow.
  const setField = <K extends keyof EventFormState>(key: K, value: EventFormState[K]) =>
    form.setFieldValue(`events[${index}].${key}` as never, value as never);

  const nonBuffet = event.form !== '' && event.form !== 'buffet';

  return (
    <div className="flex flex-col gap-8">
      {/* Row 1: Format + Product line */}
      <div className="grid grid-cols-2 gap-8">
        <EventFieldRow index={index} name="form" label={t('create_event.format')} fieldClassName={fieldClass('form')} labelClassName={labelClass('form')}>
          {({ value, onChange, invalid }) => (
            <Select
              value={value ?? ''}
              onValueChange={(newForm) => {
                onChange(newForm);
                if (newForm !== event.form) {
                  setField('lines', newForm === 'buffet' ? 0 : 1);
                  setField('ways', newForm === 'buffet' ? 0 : 1);
                }
                if (event.productLine && !productLinesForForm(newForm).includes(event.productLine)) {
                  setField('productLine', '');
                }
              }}
            >
              <SelectTrigger aria-invalid={invalid || undefined} className={cn(invalid && INVALID_TRIGGER_CLASS)}>
                <span className={cn(value ? 'text-off-black' : 'text-grey-light')}>
                  {value
                    ? DEFAULT_FORM_LABELS[value as keyof typeof DEFAULT_FORM_LABELS]
                    : t('create_event.select_format')}
                </span>
              </SelectTrigger>
              <SelectContent>
                {formValues.map((f) => (
                  <SelectItem key={f} value={f}>{DEFAULT_FORM_LABELS[f]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </EventFieldRow>

        <EventFieldRow index={index} name="productLine" label={t('create_event.product_line')} fieldClassName={fieldClass('productLine')} labelClassName={labelClass('productLine')}>
          {({ value, onChange, invalid }) => (
            <ProductLinePicker
              value={value}
              onChange={onChange}
              ariaLabel={t('create_event.product_line')}
              placeholder={t('create_event.product_line_placeholder')}
              searchPlaceholder={t('create_event.product_line_search')}
              emptyText={t('create_event.product_line_empty')}
              allLabel={t('create_event.product_line_all_tab')}
              form={event.form || undefined}
              hasError={invalid}
            />
          )}
        </EventFieldRow>
      </div>

      {/* Row 2: Guest count + Serving layout */}
      <div className="grid grid-cols-2 gap-8">
        <EventFieldRow index={index} name="paxCount" label={t('create_event.guest_count')} fieldClassName={fieldClass('paxCount')} labelClassName={labelClass('paxCount')}>
          {({ value, onChange, invalid }) => (
            <NumberStepper aria-label={t('create_event.guest_count')} value={value} onChange={onChange} invalid={invalid} />
          )}
        </EventFieldRow>

        {/* Lines + Ways share one field; the pair's message anchors on `ways`. */}
        {!nonBuffet && (
          <EventFieldRow index={index} name="ways" label={t('create_event.serving_layout')} fieldClassName={fieldClass('lines', 'ways')} labelClassName={labelClass('lines', 'ways')}>
            {({ value, onChange, invalid }) => (
              <div className="grid grid-cols-2 gap-4">
                <ServingLayoutInput label={t('create_event.lines')} value={event.lines} onChange={(lines) => setField('lines', lines)} invalid={invalid} />
                <ServingLayoutInput label={t('create_event.ways')} value={value} onChange={onChange} invalid={invalid} />
              </div>
            )}
          </EventFieldRow>
        )}
      </div>

      {/* Row 3: Tags */}
      <EventTagsField
        label={t('create_event.tags')}
        value={event.tags}
        onChange={(tags) => setField('tags', tags)}
        labelClassName={LABEL_CLASS}
      />

      {/* Row 4: Serving date + Serving time */}
      <div className="grid grid-cols-2 gap-8">
        <EventFieldRow index={index} name="scheduledAt" label={t('create_event.serving_date')} fieldClassName={fieldClass('scheduledAt')} labelClassName={labelClass('scheduledAt')}>
          {(render) => <ServingDateField {...render} />}
        </EventFieldRow>

        <EventFieldRow index={index} name="servingTime" label={t('create_event.serving_time')} labelClassName={LABEL_CLASS}>
          {({ value, onChange, invalid }) => (
            <ServingTimeDropdown value={value} onChange={onChange} invalid={invalid} />
          )}
        </EventFieldRow>
      </div>

      {/* Row 5: Event description */}
      <Field>
        <LabelWithTooltip label={t('create_event.event_description')} tooltip={t('create_event.description_tooltip')} />
        <FieldContent>
          <Textarea
            name="description"
            value={event.description}
            onChange={(e) => setField('description', e.target.value)}
            placeholder={t('create_event.description_placeholder')}
            className={TEXTAREA_CLASS}
          />
        </FieldContent>
      </Field>

      {/* Row 6: Special instructions */}
      <Field>
        <LabelWithTooltip label={t('create_event.special_instructions')} tooltip={t('create_event.instructions_tooltip')} />
        <FieldContent>
          <Textarea
            name="specialInstructions"
            value={event.specialInstructions}
            onChange={(e) => setField('specialInstructions', e.target.value)}
            placeholder={t('create_event.instructions_placeholder')}
            className={TEXTAREA_CLASS}
          />
        </FieldContent>
      </Field>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  ServingDateField — picker with closed days marked + closed-date note      */
/* -------------------------------------------------------------------------- */

function ServingDateField({ value, onChange, invalid }: EventFieldRowRender<'scheduledAt'>) {
  const { t } = useTranslation();
  const [shownMonth, setShownMonth] = useState<Date>();
  const { data: shownMonthClosures } = useClosedDates(shownMonth);
  const { data: valueMonthClosures } = useClosedDates(value);

  const closedDays = useMemo(
    () =>
      shownMonthClosures
        ? Array.from(shownMonthClosures, ([day, reasons]) => ({ date: parseISO(day), label: closureLabel(reasons, t) }))
        : undefined,
    [shownMonthClosures, t],
  );

  const valueReasons = value ? valueMonthClosures?.get(toLocalDateKey(value)) : undefined;

  return (
    <>
      <CalendarDatePicker
        value={value}
        onChange={onChange}
        placeholder={t('create_event.select_date')}
        invalid={invalid}
        closedDays={closedDays}
        onMonthChange={setShownMonth}
      />
      {valueReasons && (
        <InlineNotice className="self-start">
          {closureLabel(valueReasons, t)}
        </InlineNotice>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*  Footer — Add event + Create (Form.SubmitButton)                            */
/* -------------------------------------------------------------------------- */

interface CreateEventFooterProps {
  isPending?: boolean;
}

export function CreateEventFooter({ isPending }: CreateEventFooterProps) {
  const { t } = useTranslation();
  const { addEvent } = useCreateEventActions();

  return (
    <div className="flex w-full items-center justify-between">
      <Button        variant="tertiary"
        onClick={addEvent}
        className="transition-[background-color,transform] motion-safe:active:scale-95"
      >
        <StepperPlusIcon className="size-4" />
        {t('create_event.add_event')}
      </Button>
      <Form.SubmitButton
        mode="create"
        label={t('create_event.submit')}
        disabled={isPending}
      />
    </div>
  );
}
